import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Table } from '../../components/ui/Table';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Alert } from '../../components/ui/Alert';
import { EmptyState } from '../../components/ui/EmptyState';
import { PageHeader } from '../../components/layout/PageHeader';
import { icons } from '../../components/ui/Icons';
import { formatDate, requestJson, RequestError } from '../../lib/personnelApi';
import type { EmployeeListItem, EmployeeListQuery, EmployeeListResult, Department, Position, EmployeeStatus, EmploymentType } from '../../types/employee';

const STATUS_OPTIONS = [
  { value: 'AKTIV', label: 'Aktiv' },
  { value: 'INAKTIV', label: 'Inaktiv' },
  { value: 'BEURLAUBT', label: 'Beurlaubt' },
  { value: 'AUSGESCHIEDEN', label: 'Ausgeschieden' },
];

const EMPLOYMENT_TYPE_OPTIONS = [
  { value: 'VOLLZEIT', label: 'Vollzeit' },
  { value: 'TEILZEIT', label: 'Teilzeit' },
  { value: 'MINIJOB', label: 'Minijob' },
  { value: 'AUSBILDUNG', label: 'Ausbildung' },
  { value: 'PRAKTIKUM', label: 'Praktikum' },
  { value: 'WERKSTUDENT', label: 'Werkstudent' },
];

const STATUS_BADGE_VARIANTS: Record<EmployeeStatus, 'primary' | 'medical' | 'emergency' | 'warning' | 'gray' | 'outline'> = {
  AKTIV: 'medical',
  INAKTIV: 'gray',
  BEURLAUBT: 'warning',
  AUSGESCHIEDEN: 'emergency',
};

function Pagination({ currentPage, totalPages, totalItems, onPageChange }: {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
}) {
  if (totalPages <= 1) return null;

  const pages = [];
  const maxVisible = 5;
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  
  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1);
  }

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <div className="text-sm text-neutral-600 dark:text-neutral-400">
        Seite {currentPage} von {totalPages} · {totalItems} Einträge
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label="Vorherige Seite"
        >
          <icons.ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </Button>
        {start > 1 && (
          <>
            <Button variant="outline" size="sm" onClick={() => onPageChange(1)}>1</Button>
            {start > 2 && <span className="px-2 text-neutral-400">…</span>}
          </>
        )}
        {pages.map(page => (
          <Button
            key={page}
            variant={page === currentPage ? 'primary' : 'outline'}
            size="sm"
            onClick={() => onPageChange(page)}
          >
            {page}
          </Button>
        ))}
        {end < totalPages && (
          <>
            {end < totalPages - 1 && <span className="px-2 text-neutral-400">…</span>}
            <Button variant="outline" size="sm" onClick={() => onPageChange(totalPages)}>
              {totalPages}
            </Button>
          </>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          aria-label="Nächste Seite"
        >
          <icons.ChevronRight className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}

export function EmployeesListPage() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [query, setQuery] = useState<EmployeeListQuery>({
    search: '',
    departmentId: undefined,
    positionId: undefined,
    status: undefined,
    employmentType: undefined,
    isActive: undefined,
    page: 1,
    limit: 25,
    sortBy: 'lastName',
    sortOrder: 'asc',
  });
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== '' && value !== null) {
          params.set(key, String(value));
        }
      });
      const result = await requestJson<EmployeeListResult>(`/employees?${params.toString()}`);
      setEmployees(result.items);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch (err) {
      setError(err instanceof RequestError ? err.message : 'Fehler beim Laden der Mitarbeiter');
    } finally {
      setLoading(false);
    }
  }, [query]);

  const fetchDepartments = useCallback(async () => {
    try {
      const result = await requestJson<Department[]>(`/employees/departments`);
      setDepartments(result);
    } catch {
      // ignore
    }
  }, []);

  const fetchPositions = useCallback(async () => {
    try {
      const result = await requestJson<Position[]>(`/employees/positions`);
      setPositions(result);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    fetchDepartments();
    fetchPositions();
  }, [fetchDepartments, fetchPositions]);

  const handleSearch = (value: string) => {
    setQuery(prev => ({ ...prev, search: value, page: 1 }));
  };

  const handleFilterChange = (key: keyof EmployeeListQuery, value: string | boolean | undefined) => {
    setQuery(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setQuery(prev => ({ ...prev, page }));
  };

  const handleCreate = () => {
    navigate('/personal/mitarbeiter/neu');
  };

  const handleRowClick = (employee: EmployeeListItem) => {
    navigate(`/personal/mitarbeiter/${employee.id}`);
  };

  const columns = [
    {
      key: 'actions',
      header: '',
      width: '48px',
      render: (item: EmployeeListItem) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={e => { e.stopPropagation(); handleRowClick(item); }}
          aria-label={`Mitarbeiter ${item.displayName} öffnen`}
        >
          <icons.ChevronRight className="h-4 w-4" aria-hidden="true" />
        </Button>
      ),
    },
    {
      key: 'personnelNumber',
      header: 'Pers.-Nr.',
      render: (item: EmployeeListItem) => (
        <span className="font-mono text-sm text-neutral-600 dark:text-neutral-400">
          {item.personnelNumber || '-'}
        </span>
      ),
    },
    {
      key: 'displayName',
      header: 'Name',
      render: (item: EmployeeListItem) => (
        <div>
          <div className="font-medium text-neutral-900 dark:text-white">{item.displayName}</div>
          {(item.firstName || item.lastName) && (
            <div className="text-sm text-neutral-500 dark:text-neutral-400">
              {item.firstName} {item.lastName}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'departmentName',
      header: 'Abteilung',
      render: (item: EmployeeListItem) => item.departmentName || '-',
    },
    {
      key: 'position',
      header: 'Position',
      render: (item: EmployeeListItem) => item.position || '-',
    },
    {
      key: 'status',
      header: 'Status',
      render: (item: EmployeeListItem) => (
        <Badge variant={STATUS_BADGE_VARIANTS[item.status]}>
          {item.status}
        </Badge>
      ),
    },
    {
      key: 'email',
      header: 'Kontakt',
      render: (item: EmployeeListItem) => (
        <div className="text-sm text-neutral-600 dark:text-neutral-400">
          {item.email || '-'}
        </div>
      ),
    },
    {
      key: 'hireDate',
      header: 'Eintritt',
      render: (item: EmployeeListItem) => formatDate(item.hireDate),
    },
  ];

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <PageHeader
          title="Mitarbeiter"
          description="Übersicht aller Mitarbeiter des Klinikums"
          breadcrumbs={[
            { label: 'Startseite', href: '/' },
            { label: 'Personal', href: '/personal' },
            { label: 'Mitarbeiter' },
          ]}
        />
        <Button leftIcon="Plus" onClick={handleCreate}>
          Mitarbeiter anlegen
        </Button>
      </div>

      <Card>
        <CardHeader className="border-b border-neutral-200 dark:border-neutral-700">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Mitarbeiterübersicht</CardTitle>
              <CardDescription>Suchen, filtern und sortieren Sie die Mitarbeiterliste</CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative w-full sm:w-64">
                <icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" aria-hidden="true" />
                <Input
                  placeholder="Suchen (Name, Pers.-Nr., E-Mail, Position...)"
                  value={query.search}
                  onChange={e => handleSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select
                label=""
                value={query.departmentId || ''}
                onChange={e => handleFilterChange('departmentId', e.target.value || undefined)}
                options={[{ value: '', label: 'Alle Abteilungen' }, ...departments.map(d => ({ value: d.id, label: d.name }))]}
                placeholder="Alle Abteilungen"
              />
              <Select
                label=""
                value={query.positionId || ''}
                onChange={e => handleFilterChange('positionId', e.target.value || undefined)}
                options={[{ value: '', label: 'Alle Positionen' }, ...positions.map(position => ({ value: position.id, label: position.name }))]}
                placeholder="Alle Positionen"
              />
              <Select
                label=""
                value={query.status || ''}
                onChange={e => handleFilterChange('status', e.target.value as EmployeeStatus || undefined)}
                options={[{ value: '', label: 'Status' }, ...STATUS_OPTIONS]}
                placeholder="Status"
              />
              <Select
                label=""
                value={query.employmentType || ''}
                onChange={e => handleFilterChange('employmentType', e.target.value as EmploymentType || undefined)}
                options={[{ value: '', label: 'Beschäftigungsart' }, ...EMPLOYMENT_TYPE_OPTIONS]}
                placeholder="Beschäftigungsart"
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {loading ? (
            <Table
              columns={columns}
              data={[]}
              keyExtractor={item => item.id}
              loading
              loadingMessage="Mitarbeiter werden geladen..."
            />
          ) : error ? (
            <Alert variant="error" title="Fehler" dismissible onDismiss={() => setError(null)}>
              <p>{error}</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={fetchEmployees}>
                Erneut versuchen
              </Button>
            </Alert>
          ) : employees.length === 0 ? (
            <EmptyState
              icon="Users"
              title="Keine Mitarbeiter gefunden"
              description={query.search || query.departmentId || query.positionId || query.status ? 'Versuchen Sie, die Filter anzupassen.' : 'Noch keine Mitarbeiter angelegt. Klicken Sie auf "Mitarbeiter anlegen", um zu beginnen.'}
            />
          ) : (
            <>
              <Table
                columns={columns}
                data={employees}
                keyExtractor={item => item.id}
                rowClick={handleRowClick}
                hoverable
                striped
                compact
              />
              <div className="border-t border-neutral-200 dark:border-neutral-700 px-4 py-3">
                <Pagination
                  currentPage={query.page ?? 1}
                  totalPages={totalPages}
                  totalItems={total}
                  onPageChange={handlePageChange}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}